import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  GoogleAuthProvider,
  signInWithPopup,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User as FirebaseUser,
  sendPasswordResetEmail,
  updateProfile,
  sendEmailVerification
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp, onSnapshot, updateDoc } from 'firebase/firestore';
import { auth, db, ADMIN_EMAIL } from '../lib/firebase';
import type { User } from '../types';
import toast from 'react-hot-toast';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string, displayName: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  resendVerificationEmail: () => Promise<void>;
  logout: () => Promise<void>;
  isAuthModalOpen: boolean;
  openAuthModal: () => void;
  closeAuthModal: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const createUserProfile = async (firebaseUser: FirebaseUser, displayName?: string): Promise<User> => {
  const userRef = doc(db, 'users', firebaseUser.uid);
  const userSnap = await getDoc(userRef);

  if (!userSnap.exists()) {
    const userData: User = {
      uid: firebaseUser.uid,
      email: firebaseUser.email!,
      displayName: displayName || firebaseUser.displayName || 'User',
      photoURL: firebaseUser.photoURL || '',
      role: firebaseUser.email === ADMIN_EMAIL ? 'admin' : 'user'
    };

    if (displayName) {
      await updateProfile(firebaseUser, { displayName });
    }

    await setDoc(userRef, {
      ...userData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return userData;
  }

  return {
    ...userSnap.data(),
    uid: firebaseUser.uid
  } as User;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

  useEffect(() => {
    let unsubscribeAuth: () => void;
    let unsubscribeUser: () => void;

    const setupAuth = async () => {
      unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
        if (firebaseUser) {
          try {
            const userRef = doc(db, 'users', firebaseUser.uid);
            unsubscribeUser = onSnapshot(userRef, async (userSnap) => {
              if (userSnap.exists()) {
                const userData = { ...userSnap.data(), uid: firebaseUser.uid } as User;
                if (userData.suspended) {
                  await signOut(auth);
                  setUser(null);
                } else {
                  setUser(userData);
                }
              } else {
                setUser(await createUserProfile(firebaseUser));
              }
              setLoading(false);
            }, () => {
              setUser(null);
              setLoading(false);
            });
          } catch {
            setUser(null);
            setLoading(false);
          }
        } else {
          if (unsubscribeUser) unsubscribeUser();
          setUser(null);
          setLoading(false);
        }
      });
    };

    setupAuth();
    return () => {
      if (unsubscribeAuth) unsubscribeAuth();
      if (unsubscribeUser) unsubscribeUser();
    };
  }, []);

  const signInWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      await setDoc(doc(db, 'users', result.user.uid), {
        email: result.user.email,
        displayName: result.user.displayName,
        photoURL: result.user.photoURL,
        role: result.user.email === ADMIN_EMAIL ? 'admin' : 'user',
        lastLogin: serverTimestamp(),
        updatedAt: serverTimestamp(),
        emailVerified: true
      }, { merge: true });
      closeAuthModal();
    } catch {
      toast.error('Unable to sign in');
    }
  };

  const signInWithEmail = async (email: string, password: string) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      
      const userDoc = await getDoc(doc(db, 'users', result.user.uid));
      const userData = userDoc.data();
      const isAdmin = userData?.role === 'admin';
      
      if (!isAdmin && !result.user.emailVerified) {
        await signOut(auth);
        toast.error('Please verify your email before signing in');
        return;
      }

      await updateDoc(doc(db, 'users', result.user.uid), {
        lastLogin: serverTimestamp(),
        emailVerified: true,
        updatedAt: serverTimestamp()
      });
      
      toast.success('Successfully signed in!');
      closeAuthModal();
    } catch {
      toast.error('Invalid email or password');
    }
  };

  const signUpWithEmail = async (email: string, password: string, displayName: string) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(result.user, { displayName });
      
      const currentUser = auth.currentUser;
      let isAdminCreation = false;
      
      if (currentUser) {
        const creatorDoc = await getDoc(doc(db, 'users', currentUser.uid));
        isAdminCreation = creatorDoc.exists() && creatorDoc.data().role === 'admin';
      }

      const userData = {
        uid: result.user.uid,
        email,
        displayName,
        photoURL: '',
        role: isAdminCreation ? 'admin' : 'user',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastLogin: serverTimestamp(),
        emailVerified: isAdminCreation
      };

      await setDoc(doc(db, 'users', result.user.uid), userData);

      if (!isAdminCreation) {
        const actionCodeSettings = {
          url: 'https://www.fikraspace.com',
          handleCodeInApp: false
        };
        
        await sendEmailVerification(result.user, actionCodeSettings);
        await signOut(auth);
        toast.success('Account created! Please check your email to verify your account');
      } else {
        toast.success('Admin account created successfully');
      }
      
      closeAuthModal();
    } catch {
      toast.error('Unable to create account');
    }
  };

  const resetPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
      toast.success('Password reset email sent');
      closeAuthModal();
    } catch {
      toast.error('Unable to send reset email');
    }
  };

  const resendVerificationEmail = async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        toast.error('Please sign in first');
        return;
      }

      const actionCodeSettings = {
        url: 'https://www.fikraspace.com',
        handleCodeInApp: false
      };

      await sendEmailVerification(currentUser, actionCodeSettings);
      toast.success('Verification email sent');
    } catch {
      toast.error('Unable to send verification email');
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
    } catch {
      toast.error('Unable to sign out');
    }
  };

  const openAuthModal = () => setIsAuthModalOpen(true);
  const closeAuthModal = () => setIsAuthModalOpen(false);

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      signInWithGoogle,
      signInWithEmail,
      signUpWithEmail,
      resetPassword,
      resendVerificationEmail,
      logout,
      isAuthModalOpen,
      openAuthModal,
      closeAuthModal
    }}>
      {children}
    </AuthContext.Provider>
  );
};